import { Controller } from "@hotwired/stimulus"
import { select, pointer } from "d3-selection"
import { scaleTime, scaleLinear } from "d3-scale"
import { line as d3Line, area as d3Area, curveMonotoneX } from "d3-shape"
import { bisector } from "d3-array"
import { isDarkMode } from "../lib/helpers"
import { seriesPalette, referenceStroke } from "../lib/chart_series"

const PADDING_Y = 4
const LABEL_WIDTH = 36
const GAP = 8
const bisectDate = bisector((d) => d.date).center

export default class extends Controller {
  static values = {
    points: Array,
    windowSeconds: Number,
    references: Array,
    domain: Array,
    series: { type: String, default: "astral" },
    label: { type: String, default: "" },
  }

  connect() {
    requestAnimationFrame(() => this.#render())
    document.addEventListener("theme:changed", this.#onThemeChange)
    this.#observeResize()
    document.addEventListener("turbo:morph", this.#onMorph)
  }

  disconnect() {
    this.#resizeObserver?.disconnect()
    document.removeEventListener("theme:changed", this.#onThemeChange)
    document.removeEventListener("turbo:morph", this.#onMorph)
    this.#interaction = null
  }

  #resizeObserver
  #interaction = null
  #onMorph = () => this.#render()
  #onThemeChange = () => this.#render()

  #render() {
    this.element.innerHTML = ""
    this.element.style.position = "relative"
    this.#interaction = null
    // Scrub restore runs after the plot is rebuilt (end of #render).

    const data = this.pointsValue.map((d) => ({
      date: new Date(d.t),
      v: d.v,
    }))
    const references = this.referencesValue
    const domainOverride = this.domainValue
    const palette = seriesPalette(this.seriesValue)
    const dark = isDarkMode()
    const valueColor =
      this.seriesValue === "azure"
        ? dark
          ? "var(--color-azure-300)"
          : "var(--color-azure-700)"
        : dark
          ? "var(--color-astral-300)"
          : "var(--color-astral-700)"

    // Prefer layout size. Fall back when utilities/layout are not ready yet
    // (empty first paint, or test browsers missing tailwind height classes).
    const rect = this.element.getBoundingClientRect()
    const width = rect.width || this.element.clientWidth || 0
    const height = rect.height || this.element.clientHeight || 96
    if (!width) return

    const svgWidth = Math.max(width - LABEL_WIDTH - GAP, 1)

    const present = data.filter((d) => d.v != null)
    if (present.length < 2) return

    // Configured domain (dyno min/max) is the baseline, but always expand to
    // fit live values so a quantity above maximum cannot paint outside the plot.
    const values = present.map((d) => d.v)
    let lo, hi
    if (domainOverride.length === 2) {
      ;[lo, hi] = domainOverride
      lo = Math.min(lo, ...values)
      hi = Math.max(hi, ...values)
    } else {
      const all = [...values, ...references]
      lo = Math.min(...all)
      hi = Math.max(...all)
    }
    if (lo === hi) {
      lo -= 1
      hi += 1
    }

    const now = Date.now()
    const xScale = scaleTime()
      .domain([
        new Date(now - this.windowSecondsValue * 1000),
        new Date(now),
      ])
      .range([0, svgWidth])

    const yScale = scaleLinear()
      .domain([lo, hi])
      .range([height - PADDING_Y, PADDING_Y])

    // Clip series inside the SVG so a domain miss never paints over the caption.
    // Keep the host free of overflow:hidden so the HTML tooltip can sit above the line.
    const svg = select(this.element)
      .append("svg")
      .attr("width", svgWidth)
      .attr("height", height)
      .attr("aria-hidden", "true")
      .style("flex", "1 1 0%")
      .style("min-width", "0")
      .style("overflow", "hidden")

    const clipId = `sparkline-clip-${crypto.randomUUID()}`
    svg
      .append("defs")
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("width", svgWidth)
      .attr("height", height)

    const plot = svg.append("g").attr("clip-path", `url(#${clipId})`)

    const areaShape = d3Area()
      .defined((d) => d.v != null)
      .x((d) => xScale(d.date))
      .y0(yScale(lo))
      .y1((d) => yScale(d.v))
      .curve(curveMonotoneX)

    plot
      .append("path")
      .datum(data)
      .attr("fill", palette.band)
      .attr("d", areaShape)

    for (const v of references.filter((r) => r >= lo && r <= hi)) {
      plot
        .append("line")
        .attr("x1", 0)
        .attr("x2", svgWidth)
        .attr("y1", yScale(v))
        .attr("y2", yScale(v))
        .attr("stroke", referenceStroke(dark))
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2 3")
    }

    const lineShape = d3Line()
      .defined((d) => d.v != null)
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.v))
      .curve(curveMonotoneX)

    plot
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", palette.stroke)
      .attr("stroke-width", 1.5)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", lineShape)

    const crosshair = plot
      .append("line")
      .attr("y1", 0)
      .attr("y2", height)
      .attr(
        "stroke",
        dark ? "rgba(156, 163, 175, 0.35)" : "rgba(156, 163, 175, 0.55)"
      )
      .attr("stroke-width", 1)
      .attr("data-sparkline-crosshair", "")
      .style("display", "none")

    const focus = plot
      .append("circle")
      .attr("r", 3)
      .attr("fill", palette.stroke)
      .attr("stroke", dark ? "rgba(17, 24, 39, 1)" : "rgba(255, 255, 255, 1)")
      .attr("stroke-width", 1.5)
      .attr("data-sparkline-focus", "")
      .style("display", "none")

    const tooltip = document.createElement("div")
    tooltip.setAttribute("data-sparkline-tooltip", "")
    tooltip.style.cssText = `
      position: absolute;
      pointer-events: none;
      display: none;
      min-width: 7.5rem;
      padding: 6px 8px;
      border-radius: 6px;
      font-size: 11px;
      line-height: 1.45;
      z-index: 10;
      background: ${dark ? "rgba(31, 41, 55, 1)" : "rgba(255, 255, 255, 1)"};
      border: 1px solid ${dark ? "rgba(55, 65, 81, 1)" : "rgba(209, 213, 219, 1)"};
      color: ${dark ? "rgba(243, 244, 246, 1)" : "rgba(31, 41, 55, 1)"};
      box-shadow: 0 4px 12px rgba(0, 0, 0, ${dark ? "0.35" : "0.10"});
    `
    this.element.appendChild(tooltip)

    const showAt = (idx, { tooltip: showTooltip = true } = {}) => {
      const d = data[idx]
      if (!d || d.v == null) {
        hide()
        return
      }

      const xPos = xScale(d.date)
      const yPos = yScale(d.v)
      crosshair.style("display", null).attr("x1", xPos).attr("x2", xPos)
      focus.style("display", null).attr("cx", xPos).attr("cy", yPos)

      if (!showTooltip) {
        tooltip.style.display = "none"
        return
      }

      const titleEl = document.createElement("div")
      titleEl.style.cssText =
        "font-weight: 600; margin-bottom: 2px; white-space: nowrap;"
      titleEl.textContent = this.#formatTooltipLabel(d.date)

      const bodyEl = document.createElement("div")
      bodyEl.style.cssText = `
        display: flex;
        justify-content: space-between;
        gap: 12px;
        white-space: nowrap;
      `
      if (this.labelValue) {
        const nameEl = document.createElement("span")
        nameEl.textContent = this.labelValue
        nameEl.style.color = dark
          ? "rgba(156, 163, 175, 1)"
          : "rgba(75, 85, 99, 1)"
        bodyEl.appendChild(nameEl)
      }
      const valueEl = document.createElement("span")
      valueEl.textContent = String(d.v)
      valueEl.style.cssText = `font-weight: 600; font-variant-numeric: tabular-nums; color: ${valueColor};`
      bodyEl.appendChild(valueEl)

      tooltip.replaceChildren(titleEl, bodyEl)

      tooltip.style.visibility = "hidden"
      tooltip.style.display = "block"
      const tipWidth = tooltip.offsetWidth || 120
      const tipHeight = tooltip.offsetHeight || 40
      let left = xPos + 8
      if (left + tipWidth > svgWidth) left = xPos - tipWidth - 8
      left = Math.max(0, left)
      let top = Math.max(0, yPos - tipHeight - 6)
      if (top + tipHeight > height) top = Math.max(0, height - tipHeight)

      tooltip.style.left = `${left}px`
      tooltip.style.top = `${top}px`
      tooltip.style.visibility = "visible"
    }

    const hide = () => {
      crosshair.style("display", "none")
      focus.style("display", "none")
      tooltip.style.display = "none"
    }

    this.#interaction = { data, xScale, showAt, hide }

    svg
      .append("rect")
      .attr("width", svgWidth)
      .attr("height", height)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .style("cursor", "crosshair")
      .on("mousemove", (event) => {
        const [mx] = pointer(event)
        const tMs = xScale.invert(mx).getTime()
        this.#broadcastScrub(tMs)
      })
      .on("mouseleave", (event) => this.#broadcastHide(event))

    const labels = document.createElement("div")
    labels.style.cssText = `
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: ${LABEL_WIDTH}px;
      flex-shrink: 0;
      text-align: right;
      font-size: 10px;
      line-height: 1;
      font-variant-numeric: tabular-nums;
      color: var(--color-gray-400);
    `
    const hiSpan = document.createElement("span")
    hiSpan.textContent = Math.round(hi)
    const loSpan = document.createElement("span")
    loSpan.textContent = Math.round(lo)
    labels.append(hiSpan, loSpan)
    this.element.appendChild(labels)

    // Resize / theme / morph wipe the SVG. Re-apply scrub while the pointer
    // is still over the pair (History charts restore the same way).
    this.#restoreScrub()
  }

  // Pair metric + dyno sparklines in the same Last 2 Hours column so one
  // hover drives both crosshairs and tooltips.
  #groupRoot() {
    return this.element.closest("[data-sparkline-group]")
  }

  #pairedControllers() {
    const root = this.#groupRoot()
    const nodes = root
      ? root.querySelectorAll("[data-controller~='autoscaler-sparkline']")
      : [this.element]

    return [...nodes]
      .map((el) =>
        this.application.getControllerForElementAndIdentifier(
          el,
          "autoscaler-sparkline"
        )
      )
      .filter(Boolean)
  }

  #broadcastScrub(tMs) {
    const root = this.#groupRoot()
    if (root) root.dataset.sparklineScrubT = String(tMs)
    for (const controller of this.#pairedControllers()) {
      controller.scrubToTime(tMs, { tooltip: true })
    }
  }

  #broadcastHide(event) {
    // Stay scrubbed when the pointer moves to the sibling plot or the group gap.
    // Use relatedTarget (not :hover) so synthetic leave with null relatedTarget clears.
    const root = this.#groupRoot()
    const related = event?.relatedTarget
    if (root && related instanceof Node && root.contains(related)) return

    if (root) delete root.dataset.sparklineScrubT
    for (const controller of this.#pairedControllers()) {
      controller.hideScrub()
    }
  }

  #restoreScrub() {
    const root = this.#groupRoot()
    const tMs = root?.dataset?.sparklineScrubT
    if (tMs == null) return
    // Only restore while the pointer is still over this pair.
    if (!root.matches(":hover")) return
    this.scrubToTime(Number(tMs), { tooltip: true })
  }

  // Public so paired sparkline controllers can drive a shared scrub.
  scrubToTime(tMs, { tooltip = true } = {}) {
    const interaction = this.#interaction
    if (!interaction) return

    const idx = bisectDate(interaction.data, new Date(tMs))
    interaction.showAt(idx, { tooltip })
  }

  hideScrub() {
    this.#interaction?.hide()
  }

  #formatTooltipLabel(date) {
    const d = date instanceof Date ? date : new Date(date)
    const pad = (n) => String(n).padStart(2, "0")
    const mon = d.toLocaleString(undefined, { month: "short" })
    return `${mon} ${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  #observeResize() {
    this.#resizeObserver = new ResizeObserver(() => this.#render())
    this.#resizeObserver.observe(this.element)
  }
}
